import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { authService } from '../../services/authService';
import { normalizeOrden } from './_ordenSchema';
import { mapEstados } from '../../utils/mantenimientosKpis';
import { ESTADOS_VIGENTE_AMPLIO } from '../../utils/calcularEstado';

// ── Helpers ───────────────────────────────────────────────────────────────────

const MESES_CORTOS = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

function fmtMoney(n) {
  if (n == null || isNaN(n)) return '—';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
}
function fmtMoneyDec(n) {
  if (n == null || isNaN(n)) return '—';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n);
}
function fmtPct(n) {
  if (n == null || isNaN(n) || !isFinite(n)) return '—';
  return `${Math.round(n * 100)}%`;
}
function daysBetween(a, b) {
  if (!a || !b) return null;
  const diff = new Date(b) - new Date(a);
  return diff >= 0 ? Math.round(diff / 86400000) : null;
}
function fmtDate(d) { return d.toISOString().split('T')[0]; }

function getPeriodDates(period) {
  const now  = new Date();
  const y    = now.getFullYear();
  const m    = now.getMonth();
  switch (period) {
    case 'mes':
      return { start: fmtDate(new Date(y, m, 1)), end: fmtDate(now) };
    case 'mes_anterior':
      return { start: fmtDate(new Date(y, m - 1, 1)), end: fmtDate(new Date(y, m, 0)) };
    case 'trimestre': {
      const q = Math.floor(m / 3);
      return { start: fmtDate(new Date(y, q * 3, 1)), end: fmtDate(now) };
    }
    case 'trimestre_anterior': {
      const q     = Math.floor(m / 3);
      const pq    = q === 0 ? 3 : q - 1;
      const py    = q === 0 ? y - 1 : y;
      return { start: fmtDate(new Date(py, pq * 3, 1)), end: fmtDate(new Date(py, (pq + 1) * 3, 0)) };
    }
    case 'anio':
      return { start: fmtDate(new Date(y, 0, 1)), end: fmtDate(now) };
    case '12m':
      return { start: fmtDate(new Date(y, m - 11, 1)), end: fmtDate(now) };
    default:
      return getPeriodDates('mes');
  }
}

const PERIOD_LABELS = {
  mes:                'Este mes',
  mes_anterior:       'Mes anterior',
  trimestre:          'Este trimestre',
  trimestre_anterior: 'Trimestre anterior',
  anio:               `Año ${new Date().getFullYear()}`,
  '12m':              'Últimos 12 meses',
};

const ESTADOS_COBRADAS    = new Set(['cobrada', 'cobrada_sin_firma', 'pendiente_pago']);
const ESTADOS_ACTIVAS     = new Set(['pendiente', 'en_ruta', 'pendiente_material', 'pendiente_presupuesto', 'pendiente_sustitucion', 'ausente']);
const ESTADOS_COMPLETADAS = new Set(['cobrada', 'cobrada_sin_firma', 'pendiente_pago', 'cerrada']);
const ESTADOS_EXCLUIR     = new Set(['cancelada']);

const ESTADO_LABELS = {
  cobrada: 'Cobrada', cobrada_sin_firma: 'Cobrada s/firma',
  pendiente_pago: 'Pend. pago', pendiente: 'Pendiente',
  en_ruta: 'En ruta', pendiente_material: 'Pend. material',
  pendiente_sustitucion: 'Pend. sustitución', garantia: 'Garantía',
  ausente: 'Ausente', cerrada: 'Cerrada', cancelada: 'Cancelada',
  mantenimiento: 'Mantenimiento', instalacion: 'Instalación',
  pendiente_presupuesto: 'Pend. presupuesto',
};

const ESTADO_COLORS = {
  cobrada: 'bg-green-500', cobrada_sin_firma: 'bg-green-400',
  pendiente_pago: 'bg-yellow-400', pendiente: 'bg-blue-400',
  en_ruta: 'bg-blue-500', pendiente_material: 'bg-orange-400',
  pendiente_sustitucion: 'bg-orange-500', garantia: 'bg-purple-400',
  ausente: 'bg-red-400', cerrada: 'bg-gray-400', cancelada: 'bg-gray-300',
  mantenimiento: 'bg-teal-400', instalacion: 'bg-indigo-400',
  pendiente_presupuesto: 'bg-sky-400',
};

function tipoOrden(o) {
  const e = o.estado || '';
  const t = o.tipo   || '';
  if (e.startsWith('garantia') || t === 'garantia') return 'Garantía';
  if (e === 'mantenimiento'    || t === 'mantenimiento') return 'Mantenimiento';
  if (e === 'instalacion'      || t === 'instalacion')   return 'Instalación';
  return 'SAT';
}

// ── CSV export ────────────────────────────────────────────────────────────────

function downloadCsv(rows, filename) {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  const bom  = '﻿';
  const csv  = [
    keys.join(';'),
    ...rows.map(r => keys.map(k => `"${String(r[k] ?? '').replace(/"/g, '""')}"`).join(';')),
  ].join('\n');
  const a = Object.assign(document.createElement('a'), {
    href: URL.createObjectURL(new Blob([bom + csv], { type: 'text/csv;charset=utf-8' })),
    download: filename,
  });
  a.click();
}

// ── Primitive components ──────────────────────────────────────────────────────

function KpiCard({ label, value, sub, color = 'blue', icon }) {
  const palettes = {
    blue:   'bg-blue-50   border-blue-100   text-blue-700',
    green:  'bg-green-50  border-green-100  text-green-700',
    orange: 'bg-orange-50 border-orange-100 text-orange-700',
    purple: 'bg-purple-50 border-purple-100 text-purple-700',
    teal:   'bg-teal-50   border-teal-100   text-teal-700',
    red:    'bg-red-50    border-red-100    text-red-700',
  };
  return (
    <div className={`rounded-2xl border p-4 ${palettes[color]}`}>
      <div className="flex items-start justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider opacity-60 leading-tight">{label}</p>
        {icon && <span className="text-lg opacity-40">{icon}</span>}
      </div>
      <p className="text-2xl font-bold mt-1.5 leading-none">{value}</p>
      {sub && <p className="text-xs mt-1.5 opacity-60">{sub}</p>}
    </div>
  );
}

function BarRow({ label, value, max, formatted, color = 'bg-blue-500', subLabel }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3 py-1.5">
      <div className="w-36 flex-shrink-0">
        <span className="text-sm text-gray-700 truncate block">{label}</span>
        {subLabel && <span className="text-xs text-gray-400">{subLabel}</span>}
      </div>
      <div className="flex-1 bg-gray-100 rounded-full h-2">
        <div className={`${color} h-2 rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-sm font-semibold text-gray-800 w-24 text-right flex-shrink-0">{formatted}</span>
    </div>
  );
}

function SectionCard({ title, children, action, className = '' }) {
  return (
    <div className={`bg-white rounded-2xl border border-gray-100 shadow-sm p-5 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function Spinner() {
  return <div className="animate-spin h-8 w-8 border-2 border-blue-600 rounded-full border-t-transparent" />;
}

// ── Tab: Resumen ──────────────────────────────────────────────────────────────

function ResumenTab({ ordenes, facturas }) {
  const facts  = facturas.filter(f => f.tipo !== 'abono');
  const abonos = facturas.filter(f => f.tipo === 'abono');

  const totalFacturado = facts.reduce((s, f) => s + (Number(f.importeTotal) || 0), 0);
  const totalAbonos    = abonos.reduce((s, a) => s + (Number(a.importe)      || 0), 0);
  const netFacturado   = totalFacturado - totalAbonos;

  const activas    = ordenes.filter(o => !ESTADOS_EXCLUIR.has(o.estado));
  const cobradas   = activas.filter(o => ESTADOS_COBRADAS.has(o.estado));
  const completadas = activas.filter(o => ESTADOS_COMPLETADAS.has(o.estado));
  const tasaCierre = activas.length > 0 ? completadas.length / activas.length : 0;

  const tiemposList = completadas
    .map(o => daysBetween(o.fechaProgramada, o.fechaCierre))
    .filter(d => d != null && d >= 0 && d < 180);
  const tiempoMedio = tiemposList.length > 0
    ? Math.round(tiemposList.reduce((s, d) => s + d, 0) / tiemposList.length)
    : null;

  // Por estado
  const porEstado = {};
  activas.forEach(o => { const e = o.estado || 'desconocido'; porEstado[e] = (porEstado[e] || 0) + 1; });
  const estadosSorted = Object.entries(porEstado).sort((a, b) => b[1] - a[1]);
  const maxEstado = estadosSorted[0]?.[1] || 1;

  // Facturación por mes (últimos 6 con datos)
  const porMes = {};
  facts.forEach(f => {
    if (!f.fecha) return;
    const key = f.fecha.slice(0, 7);
    porMes[key] = (porMes[key] || 0) + (Number(f.importeTotal) || 0);
  });
  const mesesKeys = Object.keys(porMes).sort().slice(-6);
  const mesData   = mesesKeys.map(k => ({
    label: MESES_CORTOS[parseInt(k.slice(5, 7)) - 1],
    value: porMes[k],
  }));
  const maxMes = Math.max(...mesData.map(m => m.value), 1);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon="💶" label="Facturado neto" value={fmtMoney(netFacturado)}
          sub={totalAbonos > 0 ? `Abonos: -${fmtMoney(totalAbonos)}` : `${facts.length} facturas`} color="green" />
        <KpiCard icon="📋" label="Facturado bruto" value={fmtMoney(totalFacturado)}
          sub={`Media: ${fmtMoney(facts.length > 0 ? totalFacturado / facts.length : 0)}/factura`} color="blue" />
        <KpiCard icon="✅" label="Tasa de cierre" value={fmtPct(tasaCierre)}
          sub={`${completadas.length} completadas de ${activas.length}`} color="orange" />
        <KpiCard icon="⏱" label="Tiempo medio cierre" value={tiempoMedio != null ? `${tiempoMedio} días` : '—'}
          sub="desde programada hasta cierre" color="purple" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <SectionCard title="Órdenes por estado">
          {estadosSorted.length === 0
            ? <p className="text-sm text-gray-400 text-center py-6">Sin órdenes en el período</p>
            : estadosSorted.slice(0, 10).map(([e, count]) => (
              <BarRow key={e}
                label={ESTADO_LABELS[e] || e}
                value={count} max={maxEstado}
                formatted={`${count} (${fmtPct(count / activas.length)})`}
                color={ESTADO_COLORS[e] || 'bg-gray-400'}
              />
            ))
          }
        </SectionCard>

        <SectionCard title="Facturación por mes">
          {mesData.length === 0
            ? <p className="text-sm text-gray-400 text-center py-6">Sin facturas en el período</p>
            : (
              <div>
                <div className="flex items-end gap-2 h-36 mb-3">
                  {mesData.map((m, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-[10px] text-gray-500 text-center leading-tight">
                        {fmtMoney(m.value)}
                      </span>
                      <div
                        className="w-full bg-blue-500 rounded-t-md transition-all duration-500 min-h-[4px]"
                        style={{ height: `${Math.max(4, (m.value / maxMes) * 120)}px` }}
                      />
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  {mesData.map((m, i) => (
                    <div key={i} className="flex-1 text-center">
                      <span className="text-[11px] text-gray-500">{m.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )
          }
        </SectionCard>
      </div>

      {/* Resumen de cobros por método */}
      <SectionCard title="Cobros por método de pago">
        {(() => {
          const map = {};
          facts.forEach(f => {
            const k = f.metodoPago || 'No especificado';
            if (!map[k]) map[k] = { count: 0, importe: 0 };
            map[k].count++;
            map[k].importe += Number(f.importeTotal) || 0;
          });
          const entries = Object.entries(map).sort((a, b) => b[1].importe - a[1].importe);
          const maxI = Math.max(...entries.map(([, v]) => v.importe), 1);
          const COLORS = { efectivo: 'bg-green-500', tarjeta: 'bg-blue-500', garantia: 'bg-purple-400' };
          const LABELS = { efectivo: '💵 Efectivo', tarjeta: '💳 Tarjeta', garantia: '🛡 Garantía' };
          return entries.length === 0
            ? <p className="text-sm text-gray-400 text-center py-4">Sin datos</p>
            : entries.map(([k, v]) => (
              <BarRow key={k}
                label={LABELS[k] || k}
                value={v.importe} max={maxI}
                formatted={fmtMoney(v.importe)}
                subLabel={`${v.count} factura${v.count !== 1 ? 's' : ''}`}
                color={COLORS[k] || 'bg-gray-400'}
              />
            ));
        })()}
      </SectionCard>
    </div>
  );
}

// ── Tab: Facturación ──────────────────────────────────────────────────────────

function FacturacionTab({ facturas }) {
  const facts  = facturas.filter(f => f.tipo !== 'abono');
  const abonos = facturas.filter(f => f.tipo === 'abono');

  const porMes = {};
  facts.forEach(f => {
    if (!f.fecha) return;
    const key = f.fecha.slice(0, 7);
    if (!porMes[key]) porMes[key] = { facturas: 0, base: 0, iva: 0, total: 0 };
    const base  = Number(f.importe)      || 0;
    const total = Number(f.importeTotal) || 0;
    // IVA real: el campo explícito si existe; si no, total − base (respeta el
    // tipo aplicado — 21 %, 10 % o exento — en vez de asumir siempre 21 %).
    const iva = Number(f.importeIva) || (total > base ? +(total - base).toFixed(2) : 0);
    porMes[key].facturas++;
    porMes[key].base  += base;
    porMes[key].iva   += iva;
    porMes[key].total += total;
  });

  const meses = Object.keys(porMes).sort().map(k => ({
    key: k,
    label: `${MESES_CORTOS[parseInt(k.slice(5, 7)) - 1]} ${k.slice(2, 4)}`,
    ...porMes[k],
  }));

  const totalBase  = meses.reduce((s, m) => s + m.base,  0);
  const totalIva   = meses.reduce((s, m) => s + m.iva,   0);
  const totalTotal = meses.reduce((s, m) => s + m.total, 0);
  const totalFacts = meses.reduce((s, m) => s + m.facturas, 0);

  const csvMeses = meses.map(m => ({
    Mes: m.label, Facturas: m.facturas,
    'Base imponible': m.base.toFixed(2),
    'IVA': m.iva.toFixed(2),
    'Total': m.total.toFixed(2),
  }));

  return (
    <div className="space-y-4">
      <SectionCard
        title="Facturación mensual desglosada"
        action={
          <button onClick={() => downloadCsv(csvMeses, 'facturacion_mensual.csv')}
            className="text-xs text-blue-600 hover:text-blue-800 font-semibold">
            Exportar CSV
          </button>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-gray-400 text-xs">
                <th className="text-left py-2 font-semibold">Mes</th>
                <th className="text-right py-2 font-semibold">Facturas</th>
                <th className="text-right py-2 font-semibold">Base</th>
                <th className="text-right py-2 font-semibold">IVA</th>
                <th className="text-right py-2 font-semibold">Total</th>
                <th className="text-right py-2 font-semibold">Media</th>
              </tr>
            </thead>
            <tbody>
              {meses.map(m => (
                <tr key={m.key} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="py-2.5 font-medium text-gray-800">{m.label}</td>
                  <td className="py-2.5 text-right text-gray-600">{m.facturas}</td>
                  <td className="py-2.5 text-right text-gray-600">{fmtMoneyDec(m.base)}</td>
                  <td className="py-2.5 text-right text-gray-500">{fmtMoneyDec(m.iva)}</td>
                  <td className="py-2.5 text-right font-bold text-gray-900">{fmtMoney(m.total)}</td>
                  <td className="py-2.5 text-right text-gray-400">{fmtMoney(m.facturas > 0 ? m.total / m.facturas : 0)}</td>
                </tr>
              ))}
              {meses.length === 0 && (
                <tr><td colSpan={6} className="py-10 text-center text-gray-400 text-sm">Sin facturas en el período seleccionado</td></tr>
              )}
            </tbody>
            {meses.length > 0 && (
              <tfoot>
                <tr className="bg-blue-50 font-bold text-blue-900 border-t-2 border-blue-200">
                  <td className="py-2.5 pl-1 rounded-bl-lg">TOTAL</td>
                  <td className="py-2.5 text-right">{totalFacts}</td>
                  <td className="py-2.5 text-right">{fmtMoneyDec(totalBase)}</td>
                  <td className="py-2.5 text-right">{fmtMoneyDec(totalIva)}</td>
                  <td className="py-2.5 text-right text-blue-700">{fmtMoney(totalTotal)}</td>
                  <td className="py-2.5 text-right text-blue-600">{fmtMoney(totalFacts > 0 ? totalTotal / totalFacts : 0)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </SectionCard>

      {abonos.length > 0 && (
        <SectionCard title={`Abonos y devoluciones — ${abonos.length} abono${abonos.length !== 1 ? 's' : ''}`}>
          <div className="flex justify-between items-center p-3 bg-red-50 border border-red-100 rounded-xl mb-3">
            <span className="text-sm text-red-700 font-semibold">Total devuelto</span>
            <span className="font-bold text-red-700 text-lg">
              -{fmtMoney(abonos.reduce((s, a) => s + (Number(a.importe) || 0), 0))}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-gray-400 text-xs">
                  <th className="text-left py-1.5 font-semibold">Referencia</th>
                  <th className="text-left py-1.5 font-semibold">Cliente</th>
                  <th className="text-left py-1.5 font-semibold">Fecha</th>
                  <th className="text-right py-1.5 font-semibold">Importe</th>
                </tr>
              </thead>
              <tbody>
                {abonos.map(a => (
                  <tr key={a.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-2 text-gray-500 text-xs">{a.referencia || a.id?.slice(-6)}</td>
                    <td className="py-2 text-gray-700">{a.clienteNombre || '—'}</td>
                    <td className="py-2 text-gray-500">{a.fecha || '—'}</td>
                    <td className="py-2 text-right font-semibold text-red-600">-{fmtMoneyDec(a.importe)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}
    </div>
  );
}

// ── Tab: Técnicos ─────────────────────────────────────────────────────────────

function TecnicosTab({ ordenes }) {
  const activas = ordenes.filter(o => !ESTADOS_EXCLUIR.has(o.estado));

  const map = {};
  activas.forEach(o => {
    const tec = o.tecnicoNombre || 'Sin asignar';
    if (!map[tec]) map[tec] = { ordenes: 0, cobradas: 0, completadas: 0, importe: 0, tiempos: [] };
    map[tec].ordenes++;
    if (ESTADOS_COBRADAS.has(o.estado))    map[tec].cobradas++;
    if (ESTADOS_COMPLETADAS.has(o.estado)) map[tec].completadas++;
    map[tec].importe += Number(o.totalAmount) || 0;
    const d = daysBetween(o.fechaProgramada, o.fechaCierre);
    if (d != null && d >= 0 && d < 180) map[tec].tiempos.push(d);
  });

  const tecnicos = Object.entries(map).map(([nombre, d]) => ({
    nombre,
    ordenes:      d.ordenes,
    cobradas:     d.cobradas,
    completadas:  d.completadas,
    pctCobro:     d.ordenes > 0 ? d.cobradas / d.ordenes : 0,
    importe:      d.importe,
    tiempoMedio:  d.tiempos.length > 0 ? Math.round(d.tiempos.reduce((s, t) => s + t, 0) / d.tiempos.length) : null,
  })).sort((a, b) => b.ordenes - a.ordenes);

  const maxOrdenes = Math.max(...tecnicos.map(t => t.ordenes), 1);
  const maxImporte = Math.max(...tecnicos.map(t => t.importe), 1);

  const csvData = tecnicos.map(t => ({
    Técnico: t.nombre, Órdenes: t.ordenes, Cobradas: t.cobradas,
    Completadas: t.completadas, '% Cobro': `${Math.round(t.pctCobro * 100)}%`,
    'Importe (€)': t.importe.toFixed(2), 'Tiempo medio (días)': t.tiempoMedio ?? '—',
  }));

  return (
    <div className="space-y-4">
      <SectionCard
        title="Rendimiento por técnico"
        action={
          <button onClick={() => downloadCsv(csvData, 'tecnicos.csv')}
            className="text-xs text-blue-600 hover:text-blue-800 font-semibold">
            Exportar CSV
          </button>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-gray-400 text-xs">
                <th className="text-left py-2 font-semibold">Técnico</th>
                <th className="text-right py-2 font-semibold">Órdenes</th>
                <th className="text-right py-2 font-semibold">Completadas</th>
                <th className="text-right py-2 font-semibold">Cobradas</th>
                <th className="text-right py-2 font-semibold">% Cobro</th>
                <th className="text-right py-2 font-semibold">Importe</th>
                <th className="text-right py-2 font-semibold">T. cierre</th>
              </tr>
            </thead>
            <tbody>
              {tecnicos.map(t => (
                <tr key={t.nombre} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="py-3 font-semibold text-gray-800">{t.nombre}</td>
                  <td className="py-3 text-right text-gray-700">{t.ordenes}</td>
                  <td className="py-3 text-right text-gray-600">{t.completadas}</td>
                  <td className="py-3 text-right text-gray-600">{t.cobradas}</td>
                  <td className="py-3 text-right">
                    <span className={`font-bold text-sm ${
                      t.pctCobro >= 0.75 ? 'text-green-600' :
                      t.pctCobro >= 0.50 ? 'text-yellow-600' : 'text-red-500'
                    }`}>{fmtPct(t.pctCobro)}</span>
                  </td>
                  <td className="py-3 text-right font-semibold text-gray-900">{fmtMoney(t.importe)}</td>
                  <td className="py-3 text-right text-gray-500">
                    {t.tiempoMedio != null ? `${t.tiempoMedio}d` : '—'}
                  </td>
                </tr>
              ))}
              {tecnicos.length === 0 && (
                <tr><td colSpan={7} className="py-10 text-center text-gray-400">Sin órdenes en el período</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {tecnicos.length > 1 && (
          <div className="mt-4 pt-4 border-t border-gray-100 grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-2">Órdenes por técnico</p>
              {tecnicos.map(t => (
                <BarRow key={t.nombre + '-ord'}
                  label={t.nombre} value={t.ordenes} max={maxOrdenes}
                  formatted={`${t.ordenes} órd.`} color="bg-blue-500" />
              ))}
            </div>
            <div>
              <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-2">Importe generado</p>
              {tecnicos.map(t => (
                <BarRow key={t.nombre + '-imp'}
                  label={t.nombre} value={t.importe} max={maxImporte}
                  formatted={fmtMoney(t.importe)} color="bg-green-500" />
              ))}
            </div>
          </div>
        )}
      </SectionCard>
    </div>
  );
}

// ── Tab: Servicios ────────────────────────────────────────────────────────────

function ServiciosTab({ ordenes }) {
  const activas = ordenes.filter(o => !ESTADOS_EXCLUIR.has(o.estado));

  // Por tipo
  const porTipo = {};
  activas.forEach(o => {
    const t = tipoOrden(o);
    if (!porTipo[t]) porTipo[t] = { ordenes: 0, cobradas: 0, importe: 0 };
    porTipo[t].ordenes++;
    if (ESTADOS_COBRADAS.has(o.estado)) porTipo[t].cobradas++;
    porTipo[t].importe += Number(o.totalAmount) || 0;
  });
  const tipoEntries = Object.entries(porTipo).sort((a, b) => b[1].ordenes - a[1].ordenes);
  const maxTipo = Math.max(...tipoEntries.map(([, v]) => v.ordenes), 1);
  const TIPO_COLORS = { SAT: 'bg-blue-500', Garantía: 'bg-purple-500', Mantenimiento: 'bg-teal-500', Instalación: 'bg-indigo-500' };

  // Top clientes
  const clienteMap = {};
  activas.forEach(o => {
    const c = o.clienteNombre || 'Desconocido';
    if (!clienteMap[c]) clienteMap[c] = 0;
    clienteMap[c]++;
  });
  const topClientes = Object.entries(clienteMap).sort((a, b) => b[1] - a[1]).slice(0, 10);
  const maxCliente = topClientes[0]?.[1] || 1;

  // Top averías
  const averiaMap = {};
  activas.forEach(o => {
    if (!o.descripcion) return;
    const desc = o.descripcion.replace(/\n/g, ' ').trim().slice(0, 70);
    if (desc.length < 5) return;
    averiaMap[desc] = (averiaMap[desc] || 0) + 1;
  });
  const topAverias = Object.entries(averiaMap).sort((a, b) => b[1] - a[1]).slice(0, 10);
  const maxAveria = topAverias[0]?.[1] || 1;

  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <SectionCard title="Por tipo de servicio">
          {tipoEntries.length === 0
            ? <p className="text-sm text-gray-400 text-center py-6">Sin datos</p>
            : (
              <>
                {tipoEntries.map(([tipo, d]) => (
                  <BarRow key={tipo}
                    label={tipo} value={d.ordenes} max={maxTipo}
                    formatted={`${d.ordenes} órd.`}
                    subLabel={`${fmtPct(d.cobradas / d.ordenes)} cobrado`}
                    color={TIPO_COLORS[tipo] || 'bg-gray-500'}
                  />
                ))}
                <div className="mt-4 pt-3 border-t border-gray-100 grid grid-cols-2 gap-2">
                  {tipoEntries.map(([tipo, d]) => (
                    <div key={tipo} className="text-center p-3 bg-gray-50 rounded-xl">
                      <p className="text-base font-bold text-gray-800">{fmtPct(d.ordenes / activas.length)}</p>
                      <p className="text-xs text-gray-400">{tipo}</p>
                    </div>
                  ))}
                </div>
              </>
            )
          }
        </SectionCard>

        <SectionCard title="Top clientes por nº de órdenes">
          {topClientes.length === 0
            ? <p className="text-sm text-gray-400 text-center py-6">Sin datos</p>
            : topClientes.map(([nombre, count]) => (
              <BarRow key={nombre}
                label={nombre} value={count} max={maxCliente}
                formatted={`${count} órd.`} color="bg-orange-400" />
            ))
          }
        </SectionCard>
      </div>

      <SectionCard title="Descripciones de avería más frecuentes">
        {topAverias.length === 0
          ? <p className="text-sm text-gray-400 text-center py-6">Sin descripciones registradas</p>
          : topAverias.map(([desc, count]) => (
            <BarRow key={desc}
              label={desc} value={count} max={maxAveria}
              formatted={`${count}×`} color="bg-red-400" />
          ))
        }
      </SectionCard>

      {/* Export detalle órdenes */}
      <div className="flex justify-end">
        <button
          onClick={() => downloadCsv(
            activas.map(o => ({
              Referencia: o.referencia || o.id,
              Cliente: o.clienteNombre || '',
              Técnico: o.tecnicoNombre || '',
              Estado: ESTADO_LABELS[o.estado] || o.estado,
              Tipo: tipoOrden(o),
              Fecha: o.fechaProgramada || '',
              Cierre: o.fechaCierre || '',
              'Importe (€)': o.totalAmount || 0,
            })),
            'ordenes_detalle.csv'
          )}
          className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
        >
          Exportar todas las órdenes del período (CSV)
        </button>
      </div>
    </div>
  );
}

// ── Tab: Rentabilidad por técnico ─────────────────────────────────────────────

function RentabilidadTab({ ordenes, start, end }) {
  const [fichajes,       setFichajes]       = useState([]);
  const [loadingFich,    setLoadingFich]    = useState(true);

  useEffect(() => {
    getDocs(query(collection(db, 'fichajes'), orderBy('fecha', 'desc'), limit(2000)))
      .then(snap => {
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setFichajes(all.filter(f => f.fecha >= start && f.fecha <= end));
      })
      .catch(() => {})
      .finally(() => setLoadingFich(false));
  }, [start, end]);

  const tecnicos = useMemo(() => {
    const map = {};

    // Horas por técnico desde fichajes
    fichajes.forEach(f => {
      const nombre = f.tecnicoNombre || 'Sin asignar';
      if (!map[nombre]) map[nombre] = { horas: 0, ordenesCerradas: 0, importeTotal: 0 };
      map[nombre].horas += (Number(f.duracionMinutos) || 0) / 60;
    });

    // Órdenes cobradas e importe por técnico
    const cobradas = ordenes.filter(o => ESTADOS_COBRADAS.has(o.estado));
    cobradas.forEach(o => {
      const nombre = o.tecnicoNombre || 'Sin asignar';
      if (!map[nombre]) map[nombre] = { horas: 0, ordenesCerradas: 0, importeTotal: 0 };
      map[nombre].ordenesCerradas++;
      map[nombre].importeTotal += Number(o.totalAmount) || 0;
    });

    return Object.entries(map)
      .filter(([, d]) => d.horas > 0 || d.ordenesCerradas > 0)
      .map(([nombre, d]) => ({
        nombre,
        horas:            Math.round(d.horas * 10) / 10,
        ordenesCerradas:  d.ordenesCerradas,
        importeTotal:     d.importeTotal,
        euroPorHora:      d.horas > 0 ? d.importeTotal / d.horas : 0,
        ordenesPorHora:   d.horas > 0 ? d.ordenesCerradas / d.horas : 0,
      }))
      .sort((a, b) => b.euroPorHora - a.euroPorHora);
  }, [fichajes, ordenes]);

  const maxHoras   = Math.max(...tecnicos.map(t => t.horas), 1);
  const maxImporte = Math.max(...tecnicos.map(t => t.importeTotal), 1);
  const maxEph     = Math.max(...tecnicos.map(t => t.euroPorHora), 1);

  const csvData = tecnicos.map(t => ({
    Técnico:              t.nombre,
    'Horas fichadas':     t.horas.toFixed(1),
    'Órdenes cobradas':   t.ordenesCerradas,
    'Importe total (€)':  t.importeTotal.toFixed(2),
    '€ / hora':           t.euroPorHora.toFixed(2),
    'Órdenes / hora':     t.ordenesPorHora.toFixed(2),
  }));

  if (loadingFich) return (
    <div className="flex items-center justify-center py-20 gap-3 text-gray-500">
      <Spinner />
      <span className="text-sm">Cargando fichajes…</span>
    </div>
  );

  return (
    <div className="space-y-4">
      <SectionCard
        title="Rentabilidad por técnico"
        action={
          <button onClick={() => downloadCsv(csvData, 'rentabilidad.csv')}
            className="text-xs text-blue-600 hover:text-blue-800 font-semibold">
            Exportar CSV
          </button>
        }
      >
        {tecnicos.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">Sin datos de fichajes o cobros en el período</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-gray-400 text-xs">
                    <th className="text-left py-2 font-semibold">Técnico</th>
                    <th className="text-right py-2 font-semibold">Horas</th>
                    <th className="text-right py-2 font-semibold">Órd. cobradas</th>
                    <th className="text-right py-2 font-semibold">Importe generado</th>
                    <th className="text-right py-2 font-semibold">€ / hora</th>
                    <th className="text-right py-2 font-semibold">Órd. / hora</th>
                  </tr>
                </thead>
                <tbody>
                  {tecnicos.map(t => (
                    <tr key={t.nombre} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-3 font-semibold text-gray-800">{t.nombre}</td>
                      <td className="py-3 text-right text-gray-600">{t.horas.toFixed(1)}h</td>
                      <td className="py-3 text-right text-gray-600">{t.ordenesCerradas}</td>
                      <td className="py-3 text-right font-semibold text-gray-900">{fmtMoney(t.importeTotal)}</td>
                      <td className="py-3 text-right">
                        <span className={`font-bold ${t.euroPorHora >= 50 ? 'text-green-600' : t.euroPorHora >= 25 ? 'text-yellow-600' : 'text-red-500'}`}>
                          {fmtMoneyDec(t.euroPorHora)}
                        </span>
                      </td>
                      <td className="py-3 text-right text-gray-500">{t.ordenesPorHora.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {tecnicos.length > 1 && (
              <div className="mt-4 pt-4 border-t border-gray-100 grid md:grid-cols-3 gap-6">
                <div>
                  <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-2">Horas trabajadas</p>
                  {tecnicos.map(t => (
                    <BarRow key={t.nombre + '-h'} label={t.nombre} value={t.horas} max={maxHoras}
                      formatted={`${t.horas.toFixed(1)}h`} color="bg-indigo-500" />
                  ))}
                </div>
                <div>
                  <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-2">Importe generado</p>
                  {tecnicos.map(t => (
                    <BarRow key={t.nombre + '-i'} label={t.nombre} value={t.importeTotal} max={maxImporte}
                      formatted={fmtMoney(t.importeTotal)} color="bg-green-500" />
                  ))}
                </div>
                <div>
                  <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-2">€ por hora</p>
                  {tecnicos.map(t => (
                    <BarRow key={t.nombre + '-eph'} label={t.nombre} value={t.euroPorHora} max={maxEph}
                      formatted={`${Math.round(t.euroPorHora)}€/h`} color="bg-amber-500" />
                  ))}
                </div>
              </div>
            )}

            <div className="mt-4 pt-3 border-t border-gray-100 bg-blue-50 rounded-xl p-3">
              <p className="text-xs text-blue-600 font-semibold">ℹ️ Nota sobre el cálculo</p>
              <p className="text-xs text-blue-500 mt-0.5">Horas: suma de fichajes cerrados en el período. Importe: órdenes con estado cobrado/cobrado-sin-firma. El importe de la orden viene de StelOrder (campo total-amount).</p>
            </div>
          </>
        )}
      </SectionCard>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

// ── Tab: Presupuestos ─────────────────────────────────────────────────────────

function PresupuestosTab({ presupuestos, loading }) {
  if (loading) return <div className="flex justify-center py-20"><Spinner /></div>;
  if (!presupuestos.length) return <p className="text-sm text-gray-400 text-center py-20">Sin datos de presupuestos</p>;

  const ESTADOS_PRES = {
    borrador:   { label: 'Borrador',    color: 'bg-gray-400' },
    enviado:    { label: 'Enviado',     color: 'bg-blue-500' },
    aceptado:   { label: 'Aceptado',    color: 'bg-green-500' },
    rechazado:  { label: 'Rechazado',   color: 'bg-red-500' },
    expirado:   { label: 'Expirado',    color: 'bg-orange-400' },
    convertido: { label: 'Convertido',  color: 'bg-teal-500' },
  };

  const total = presupuestos.length;
  const porEstado = {};
  presupuestos.forEach(p => {
    const e = p.estado || 'borrador';
    if (!porEstado[e]) porEstado[e] = { count: 0, importe: 0 };
    porEstado[e].count++;
    porEstado[e].importe += Number(p.total || 0);
  });

  const aceptados  = (porEstado.aceptado?.count || 0) + (porEstado.convertido?.count || 0);
  const rechazados = porEstado.rechazado?.count || 0;
  const enviados   = porEstado.enviado?.count || 0;
  const decididos  = aceptados + rechazados;
  const tasaConversion = decididos > 0 ? aceptados / decididos : null;

  const importeAceptado = (porEstado.aceptado?.importe || 0) + (porEstado.convertido?.importe || 0);
  const importeTotal    = Object.values(porEstado).reduce((s, v) => s + v.importe, 0);

  // Tiempo medio de respuesta (días entre fecha y updatedAt para aceptados/rechazados)
  const tiempos = presupuestos
    .filter(p => ['aceptado', 'rechazado', 'convertido'].includes(p.estado) && p.fecha && p.updatedAt)
    .map(p => Math.max(0, (new Date(p.updatedAt) - new Date(p.fecha)) / 86400000))
    .filter(d => d >= 0 && d < 180);
  const tiempoMedio = tiempos.length > 0
    ? Math.round(tiempos.reduce((s, d) => s + d, 0) / tiempos.length)
    : null;

  const maxCount = Math.max(...Object.values(porEstado).map(v => v.count), 1);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Total presupuestos" value={total} icon="📋" color="blue" />
        <KpiCard label="Tasa de conversión" value={tasaConversion != null ? fmtPct(tasaConversion) : '—'} sub={`${aceptados} aceptados / ${decididos} decididos`} icon="✅" color="green" />
        <KpiCard label="Importe aceptado" value={fmtMoney(importeAceptado)} sub={`de ${fmtMoney(importeTotal)} total`} icon="💶" color="teal" />
        <KpiCard label="Tiempo medio respuesta" value={tiempoMedio != null ? `${tiempoMedio}d` : '—'} sub="desde envío hasta decisión" icon="⏱" color="purple" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <SectionCard title="Distribución por estado">
          {Object.entries(porEstado).sort((a, b) => b[1].count - a[1].count).map(([estado, data]) => (
            <BarRow
              key={estado}
              label={(ESTADOS_PRES[estado]?.label || estado)}
              value={data.count} max={maxCount}
              formatted={`${data.count} (${fmtMoney(data.importe)})`}
              color={ESTADOS_PRES[estado]?.color || 'bg-gray-400'}
            />
          ))}
        </SectionCard>

        <SectionCard title="Estado actual del pipeline">
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Pendientes respuesta', value: enviados, sub: 'estado enviado', color: 'blue' },
              { label: 'Aceptados', value: aceptados, color: 'green' },
              { label: 'Rechazados', value: rechazados, color: 'red' },
              { label: 'Expirados', value: porEstado.expirado?.count || 0, color: 'orange' },
            ].map(k => (
              <KpiCard key={k.label} label={k.label} value={k.value} sub={k.sub} color={k.color} />
            ))}
          </div>
        </SectionCard>
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => downloadCsv(
            presupuestos.map(p => ({
              Numero: p.numero || p.id,
              Cliente: p.clienteNombre || p.cliente || '',
              Estado: p.estado || '',
              Fecha: p.fecha || '',
              Total: p.total || 0,
              Titulo: p.titulo || p.descripcion || '',
            })),
            'presupuestos.csv'
          )}
          className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
        >
          Exportar CSV
        </button>
      </div>
    </div>
  );
}

// ── Tab: Mantenimientos ARR ───────────────────────────────────────────────────

function MantenimientosARRTab({ mantenimientos, loading }) {
  if (loading) return <div className="flex justify-center py-20"><Spinner /></div>;
  if (!mantenimientos.length) return <p className="text-sm text-gray-400 text-center py-20">Sin datos de contratos</p>;

  // `mantenimientos` llega en bruto de getMantenimientos.js (mismo endpoint
  // que MantenimientosView.js/DashboardAtencionCliente.js) — su campo
  // `estado` es el valor guardado tal cual, no el calculado por fechas
  // (calcularEstado). Antes esta pestaña comprobaba un Set de estados en
  // bruto retipeado a mano, que se había quedado sin 'activo' (!), 'vencido',
  // 'pendiente_renovar', 'pendiente_reagendar' y 'sepa_devuelto' — un ARR
  // bastante por debajo del real. mapEstados() + ESTADOS_VIGENTE_AMPLIO es
  // el mismo criterio ya unificado en el resto de pantallas.
  const data = mapEstados(mantenimientos);
  const activos = data.filter(m => ESTADOS_VIGENTE_AMPLIO.includes(m.estado));
  const arr     = activos.reduce((s, m) => s + Number(m.precioAnual || 0), 0);
  const mrr     = arr / 12;

  const hoy = new Date().toISOString().split('T')[0];
  const en30  = new Date(); en30.setDate(en30.getDate() + 30);  const str30  = en30.toISOString().split('T')[0];
  const en60  = new Date(); en60.setDate(en60.getDate() + 60);  const str60  = en60.toISOString().split('T')[0];
  const en90  = new Date(); en90.setDate(en90.getDate() + 90);  const str90  = en90.toISOString().split('T')[0];

  const renovaciones30 = activos.filter(m => m.fechaProximoMantenimiento && m.fechaProximoMantenimiento <= str30 && m.fechaProximoMantenimiento >= hoy);
  const renovaciones60 = activos.filter(m => m.fechaProximoMantenimiento && m.fechaProximoMantenimiento > str30 && m.fechaProximoMantenimiento <= str60);
  const renovaciones90 = activos.filter(m => m.fechaProximoMantenimiento && m.fechaProximoMantenimiento > str60 && m.fechaProximoMantenimiento <= str90);

  const arrEnRiesgo = data.filter(m => ['pendiente_renovar', 'vencido', 'sepa_devuelto'].includes(m.estado))
    .reduce((s, m) => s + Number(m.precioAnual || 0), 0);

  // Por pack
  const porPack = {};
  activos.forEach(m => {
    const p = m.pack || 'sin_pack';
    if (!porPack[p]) porPack[p] = { count: 0, arr: 0 };
    porPack[p].count++;
    porPack[p].arr += Number(m.precioAnual || 0);
  });
  const maxPack = Math.max(...Object.values(porPack).map(v => v.count), 1);

  const PACK_LABELS = { basico: 'Básico', silver: 'Silver', gold: 'Gold', comunidad: 'Comunidad', sin_pack: 'Sin pack' };
  const PACK_COLORS = { basico: 'bg-blue-500', silver: 'bg-purple-500', gold: 'bg-amber-500', comunidad: 'bg-green-500', sin_pack: 'bg-gray-400' };

  const bajas = data.filter(m => m.estado === 'baja').length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="ARR total" value={fmtMoney(arr)} sub={`${activos.length} contratos activos`} icon="📈" color="green" />
        <KpiCard label="MRR" value={fmtMoney(mrr)} sub="facturación mensual recurrente" icon="💶" color="teal" />
        <KpiCard label="ARR en riesgo" value={fmtMoney(arrEnRiesgo)} sub="pendiente renovar / devuelto / vencido" icon="⚠️" color="orange" />
        <KpiCard label="Bajas" value={bajas} sub="contratos dados de baja" icon="📉" color="red" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <SectionCard title="Por pack">
          {Object.entries(porPack).sort((a, b) => b[1].count - a[1].count).map(([pack, data]) => (
            <BarRow
              key={pack}
              label={PACK_LABELS[pack] || pack}
              value={data.count} max={maxPack}
              formatted={`${data.count} · ${fmtMoney(data.arr)}/año`}
              color={PACK_COLORS[pack] || 'bg-gray-400'}
            />
          ))}
        </SectionCard>

        <SectionCard title="Próximas revisiones">
          <div className="space-y-3">
            {[
              { label: 'Próximos 30 días', items: renovaciones30, color: 'bg-red-100 border-red-200 text-red-800' },
              { label: 'Próximos 31-60 días', items: renovaciones60, color: 'bg-orange-100 border-orange-200 text-orange-800' },
              { label: 'Próximos 61-90 días', items: renovaciones90, color: 'bg-yellow-100 border-yellow-200 text-yellow-800' },
            ].map(b => (
              <div key={b.label} className={`rounded-xl border px-4 py-3 ${b.color}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold">{b.label}</span>
                  <span className="text-lg font-bold">{b.items.length}</span>
                </div>
                {b.items.length > 0 && (
                  <p className="text-xs mt-1 opacity-70">
                    {b.items.slice(0, 3).map(m => m.clienteNombre).join(', ')}{b.items.length > 3 ? ` y ${b.items.length - 3} más` : ''}
                  </p>
                )}
              </div>
            ))}
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

// ── Tab: Comparativa año anterior ─────────────────────────────────────────────

function ComparativaTab({ allOrdenes, allFacturas, start, end }) {
  // Calcular período anterior (mismo período del año pasado)
  const startPrev = start.replace(/^(\d{4})/, y => String(Number(y) - 1));
  const endPrev   = end.replace(/^(\d{4})/, y => String(Number(y) - 1));

  const filtrarPeriodo = (list, dateField, s, e) =>
    list.filter(o => { const d = o[dateField] || ''; return d >= s && d <= e; });

  const ordenes     = filtrarPeriodo(allOrdenes,  'fechaProgramada', start,     end);
  const ordenesPrev = filtrarPeriodo(allOrdenes,  'fechaProgramada', startPrev, endPrev);
  const facturas    = filtrarPeriodo(allFacturas, 'fecha',           start,     end);
  const facturasPrev = filtrarPeriodo(allFacturas, 'fecha',          startPrev, endPrev);

  const EXCLUIR = new Set(['cancelada']);
  const COBRADAS = new Set(['cobrada', 'cobrada_sin_firma', 'pendiente_pago']);

  const stats = (ords, facts) => {
    const activas  = ords.filter(o => !EXCLUIR.has(o.estado));
    const cobradas = activas.filter(o => COBRADAS.has(o.estado));
    const filt     = facts.filter(f => f.tipo !== 'abono');
    const facturado = filt.reduce((s, f) => s + (Number(f.importeTotal) || 0), 0);
    return { total: activas.length, cobradas: cobradas.length, facturado, facturas: filt.length };
  };

  const curr = stats(ordenes,     facturas);
  const prev = stats(ordenesPrev, facturasPrev);

  const delta = (a, b) => {
    if (b === 0) return a > 0 ? '+∞' : '—';
    const pct = ((a - b) / b) * 100;
    return `${pct >= 0 ? '+' : ''}${Math.round(pct)}%`;
  };
  const deltaColor = (a, b) => b === 0 ? 'text-gray-400' : a >= b ? 'text-green-600' : 'text-red-600';

  const rows = [
    { label: 'Órdenes gestionadas', curr: curr.total,    prev: prev.total    },
    { label: 'Órdenes cobradas',    curr: curr.cobradas, prev: prev.cobradas },
    { label: 'Facturas emitidas',   curr: curr.facturas, prev: prev.facturas },
    { label: 'Total facturado (€)', curr: curr.facturado, prev: prev.facturado, money: true },
  ];

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 text-sm text-blue-700">
        Comparando <strong>{start} → {end}</strong> con <strong>{startPrev} → {endPrev}</strong>
      </div>

      <SectionCard title="Comparativa período actual vs año anterior">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 text-xs text-gray-500 font-semibold">Métrica</th>
                <th className="text-right py-2 text-xs text-gray-500 font-semibold">Año anterior</th>
                <th className="text-right py-2 text-xs text-gray-500 font-semibold">Período actual</th>
                <th className="text-right py-2 text-xs text-gray-500 font-semibold">Variación</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.label} className="border-b border-gray-50">
                  <td className="py-3 font-medium text-gray-800">{r.label}</td>
                  <td className="py-3 text-right text-gray-500">
                    {r.money ? fmtMoney(r.prev) : r.prev}
                  </td>
                  <td className="py-3 text-right font-bold text-gray-900">
                    {r.money ? fmtMoney(r.curr) : r.curr}
                  </td>
                  <td className={`py-3 text-right font-bold text-sm ${deltaColor(r.curr, r.prev)}`}>
                    {delta(r.curr, r.prev)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <SectionCard title="Cobradas por mes — actual vs anterior">
        {[...new Set([...ordenes, ...ordenesPrev].map(o => (o.fechaProgramada || '').slice(0, 7)))].sort().reverse().slice(0, 6).reverse().map(mes => {
          const c = ordenes.filter(o => (o.fechaProgramada || '').startsWith(mes) && COBRADAS.has(o.estado)).length;
          const prevMes = mes.replace(/^(\d{4})/, y => String(Number(y) - 1));
          const p = ordenesPrev.filter(o => (o.fechaProgramada || '').startsWith(prevMes) && COBRADAS.has(o.estado)).length;
          const maxV = Math.max(c, p, 1);
          return (
            <div key={mes} className="mb-3">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                <span>{mes}</span>
                <span className={deltaColor(c, p)}>{delta(c, p)}</span>
              </div>
              <div className="flex gap-1 items-center">
                <div className="flex-1 bg-gray-100 rounded-full h-2">
                  <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${(p / maxV) * 100}%` }} />
                </div>
                <span className="text-xs text-gray-400 w-6 text-right">{p}</span>
                <div className="w-px h-4 bg-gray-200" />
                <div className="flex-1 bg-gray-100 rounded-full h-2">
                  <div className="bg-green-500 h-2 rounded-full" style={{ width: `${(c / maxV) * 100}%` }} />
                </div>
                <span className="text-xs text-gray-800 font-bold w-6 text-right">{c}</span>
              </div>
            </div>
          );
        })}
        <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
          <div className="flex items-center gap-1.5"><div className="w-3 h-2 rounded bg-blue-400" /><span>Año anterior</span></div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-2 rounded bg-green-500" /><span>Actual</span></div>
        </div>
      </SectionCard>
    </div>
  );
}

// ── Tab: Marketing / Leads ───────────────────────────────────────────────────

const LEAD_ESTADOS = [
  { key: 'nuevo',      label: 'Nuevos',      color: 'bg-blue-500'  },
  { key: 'contactado', label: 'Contactados', color: 'bg-amber-500' },
  { key: 'convertido', label: 'Convertidos', color: 'bg-green-500' },
  { key: 'descartado', label: 'Descartados', color: 'bg-gray-400'  },
];

function MarketingTab({ leads, loading, period, start, end }) {
  const spendKey = `informes_marketing_inversion_${period}`;
  const [inversion, setInversion] = useState('');

  useEffect(() => {
    try { setInversion(localStorage.getItem(spendKey) || ''); } catch { setInversion(''); }
  }, [spendKey]);

  const onInversion = (v) => {
    setInversion(v);
    try { localStorage.setItem(spendKey, v); } catch { /* private mode */ }
  };

  if (loading) return <div className="flex justify-center py-20"><Spinner /></div>;

  const enRango = leads.filter(l => {
    const d = String(l.createdAt || '').slice(0, 10);
    return d && d >= start && d <= end;
  });

  if (!enRango.length) {
    return (
      <p className="text-sm text-gray-400 text-center py-20">
        Sin leads en este periodo{leads.length ? ` (${leads.length} en total fuera de rango)` : ''}
      </p>
    );
  }

  const estadoDe = l => (l.status || 'nuevo');
  const byEstado = Object.fromEntries(LEAD_ESTADOS.map(e => [e.key, enRango.filter(l => estadoDe(l) === e.key).length]));
  const total        = enRango.length;
  const convertidos  = byEstado.convertido || 0;
  const descartados  = byEstado.descartado || 0;
  const gestionables = total - descartados;
  const tasaConv     = gestionables > 0 ? convertidos / gestionables : 0;

  const spend = Number(String(inversion).replace(',', '.')) || 0;
  const cpl   = spend > 0 && total > 0       ? spend / total       : null;
  const cac   = spend > 0 && convertidos > 0 ? spend / convertidos : null;

  const conError = enRango.filter(l => l.error && String(l.error).trim());
  const nuevosClientes = enRango.filter(l => l.isNewClient === true || l.isNewClient === 'true').length;

  const agrupar = (campo, fallback) => {
    const m = {};
    enRango.forEach(l => {
      const k = (String(l[campo] || '').trim()) || fallback;
      if (!m[k]) m[k] = { count: 0, conv: 0 };
      m[k].count++;
      if (estadoDe(l) === 'convertido') m[k].conv++;
    });
    return Object.entries(m).sort((a, b) => b[1].count - a[1].count);
  };

  const porServicio = agrupar('service', 'Sin especificar');
  const porLocalidad = agrupar('city', 'Sin localidad').slice(0, 8);
  const porAnuncio = agrupar('adId', '').filter(([k]) => k).slice(0, 8);

  const maxEstado   = Math.max(total, 1);
  const maxServicio = Math.max(...porServicio.map(([, v]) => v.count), 1);
  const maxLocalidad = Math.max(...porLocalidad.map(([, v]) => v.count), 1);
  const maxAnuncio  = Math.max(...porAnuncio.map(([, v]) => v.count), 1);

  const exportar = () => downloadCsv(
    enRango.map(l => ({
      fecha: String(l.createdAt || '').slice(0, 10),
      nombre: l.name || '', telefono: l.phone || '', email: l.email || '',
      cp: l.zipCode || '', localidad: l.city || '', servicio: l.service || '',
      estado: estadoDe(l), anuncio: l.adId || '',
      cliente_nuevo: l.isNewClient ? 'sí' : 'no',
      stelorder_cliente: l.stelOrderClientId || '', stelorder_incidencia: l.stelOrderIncidentRef || '',
      error: l.error || '',
    })),
    `leads_${start}_${end}.csv`,
  );

  return (
    <div className="space-y-4">
      {/* Inversión publicitaria (manual) */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex flex-wrap items-center gap-3">
        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
          Inversión publicitaria del periodo
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">€</span>
          <input
            type="number" min="0" step="10" inputMode="decimal"
            value={inversion}
            onChange={e => onInversion(e.target.value)}
            placeholder="0"
            className="w-36 pl-7 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300"
          />
        </div>
        <span className="text-xs text-gray-400">
          Se guarda en este navegador. Facebook/Meta no envía el gasto por la API — introdúcelo a mano para ver coste por lead y CAC.
        </span>
        <button onClick={exportar}
          className="ml-auto text-xs font-semibold text-blue-600 hover:text-blue-800">
          Exportar CSV
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard label="Leads" value={total} sub={`${nuevosClientes} clientes nuevos`} icon="🎯" color="blue" />
        <KpiCard label="Convertidos" value={convertidos} sub={`${byEstado.nuevo || 0} sin contactar`} icon="✅" color="green" />
        <KpiCard label="Conversión" value={fmtPct(tasaConv)} sub="sobre leads no descartados" icon="📈" color="teal" />
        <KpiCard label="Coste / lead" value={cpl != null ? fmtMoney(cpl) : '—'} sub={spend > 0 ? `inversión ${fmtMoney(spend)}` : 'falta inversión'} icon="💸" color="orange" />
        <KpiCard label="CAC" value={cac != null ? fmtMoney(cac) : '—'} sub="coste por conversión" icon="💶" color="purple" />
        <KpiCard label="Con error" value={conError.length} sub="fallo al crear en StelOrder" icon="⚠️" color={conError.length ? 'red' : 'green'} />
      </div>

      {/* Embudo */}
      <SectionCard title="Embudo de leads">
        {LEAD_ESTADOS.map(e => (
          <BarRow
            key={e.key}
            label={e.label}
            value={byEstado[e.key] || 0} max={maxEstado}
            formatted={`${byEstado[e.key] || 0} · ${fmtPct(total > 0 ? (byEstado[e.key] || 0) / total : 0)}`}
            color={e.color}
          />
        ))}
      </SectionCard>

      <div className="grid md:grid-cols-2 gap-4">
        <SectionCard title="Por servicio interesado">
          {porServicio.map(([serv, d]) => (
            <BarRow
              key={serv}
              label={serv}
              value={d.count} max={maxServicio}
              formatted={`${d.count} · ${d.conv} conv.`}
              color="bg-blue-500"
            />
          ))}
        </SectionCard>

        <SectionCard title="Por localidad (top 8)">
          {porLocalidad.map(([loc, d]) => (
            <BarRow
              key={loc}
              label={loc}
              value={d.count} max={maxLocalidad}
              formatted={`${d.count} · ${d.conv} conv.`}
              color="bg-teal-500"
            />
          ))}
        </SectionCard>
      </div>

      {porAnuncio.length > 0 && (
        <SectionCard title="Por anuncio (adId, top 8)">
          {porAnuncio.map(([ad, d]) => (
            <BarRow
              key={ad}
              label={ad}
              value={d.count} max={maxAnuncio}
              formatted={`${d.count} leads · ${d.conv} conv. · ${fmtPct(d.count > 0 ? d.conv / d.count : 0)}`}
              color="bg-purple-500"
            />
          ))}
        </SectionCard>
      )}

      {conError.length > 0 && (
        <SectionCard title={`Leads con error de integración (${conError.length})`}>
          <p className="text-xs text-gray-400 mb-3">
            Estos leads llegaron pero falló la creación automática de cliente/incidencia en StelOrder.
            Revísalos y créalos a mano para no perder la oportunidad.
          </p>
          <div className="space-y-2">
            {conError.slice(0, 20).map(l => (
              <div key={l.id} className="flex items-start justify-between gap-3 py-2 border-b border-gray-50 last:border-0">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">{l.name || 'Sin nombre'} · {l.phone || 's/tel'}</p>
                  <p className="text-xs text-red-600 truncate">{l.error}</p>
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0">{String(l.createdAt || '').slice(0, 10)}</span>
              </div>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
}

// ── Tab: Fichajes vs Comisiones ──────────────────────────────────────────────
// Auditoría §16/§31: cruzar horas fichadas (RDL 8/2019) con comisión del mes
// por técnico — dos números de "cuánto trabajó" que nunca se veían juntos.
function FichajesComisionTab({ fichajes, comisiones, loading, mes }) {
  if (loading) return <div className="flex justify-center py-20"><Spinner /></div>;

  const porTec = {};
  const key = (id, nombre) => {
    const k = String(id || nombre || '?');
    if (!porTec[k]) porTec[k] = { nombre: nombre || `ID ${id}`, minutos: 0, dias: new Set(), comPagada: 0, comPendiente: 0, nCom: 0 };
    return porTec[k];
  };

  fichajes.forEach(f => {
    const t = key(f.tecnicoId, f.tecnicoNombre);
    t.minutos += Number(f.duracionMinutos) || 0;
    if (f.fecha) t.dias.add(f.fecha);
  });
  comisiones.forEach(c => {
    const enMes = String(c.fechaCreacion || c.fecha || '').slice(0, 7) === mes;
    if (!enMes) return;
    const t = key(c.employeeId, c.employeeName);
    const imp = Number(c.importe) || 0;
    if ((c.estado || '').toUpperCase() === 'PAGADA') t.comPagada += imp; else t.comPendiente += imp;
    t.nCom++;
  });

  const filas = Object.values(porTec)
    .map(t => {
      const horas = t.minutos / 60;
      const comTotal = t.comPagada + t.comPendiente;
      return { ...t, horas, dias: t.dias.size, comTotal, eurHora: horas > 0 ? comTotal / horas : null };
    })
    .filter(t => t.horas > 0 || t.comTotal > 0)
    .sort((a, b) => b.horas - a.horas);

  if (filas.length === 0) {
    return <p className="text-sm text-gray-400 text-center py-20">Sin datos de fichajes ni comisiones para {mes}</p>;
  }

  const totHoras = filas.reduce((s, t) => s + t.horas, 0);
  const totCom   = filas.reduce((s, t) => s + t.comTotal, 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <KpiCard label={`Horas fichadas ${mes}`} value={`${Math.round(totHoras)} h`} sub={`${filas.length} técnicos`} icon="⏱️" color="blue" />
        <KpiCard label="Comisión del mes" value={fmtMoney(totCom)} sub="pagada + pendiente" icon="💶" color="teal" />
        <KpiCard label="€ comisión / hora" value={totHoras > 0 ? fmtMoneyDec(totCom / totHoras) : '—'} sub="media del equipo" icon="⚖️" color="purple" />
      </div>

      <SectionCard title={`Por técnico · ${mes}`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="text-xs text-gray-400 uppercase tracking-wider">
                <th className="text-left py-2">Técnico</th>
                <th className="text-right py-2">Horas</th>
                <th className="text-right py-2">Días</th>
                <th className="text-right py-2">Comisión pagada</th>
                <th className="text-right py-2">Pendiente</th>
                <th className="text-right py-2">€/hora</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((t, i) => (
                <tr key={i} className="border-t border-gray-50">
                  <td className="py-2.5 font-semibold text-gray-800">{t.nombre}</td>
                  <td className="py-2.5 text-right text-gray-700">{t.horas.toFixed(1)}</td>
                  <td className="py-2.5 text-right text-gray-500">{t.dias}</td>
                  <td className="py-2.5 text-right text-green-700">{fmtMoneyDec(t.comPagada)}</td>
                  <td className="py-2.5 text-right text-amber-600">{fmtMoneyDec(t.comPendiente)}</td>
                  <td className="py-2.5 text-right font-semibold text-gray-800">{t.eurHora != null ? fmtMoneyDec(t.eurHora) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-400 mt-3">
          Las comisiones se cuentan por su fecha de creación en {mes}. Horas = suma de <code>duracionMinutos</code> de los fichajes cerrados.
        </p>
      </SectionCard>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function InformesView() {
  const [period,            setPeriod]            = useState('mes');
  const [activeTab,         setActiveTab]         = useState('resumen');
  const [allOrdenes,        setAllOrdenes]        = useState([]);
  const [allFacturas,       setAllFacturas]       = useState([]);
  const [allPresupuestos,   setAllPresupuestos]   = useState(null);
  const [allMantenimientos, setAllMantenimientos] = useState(null);
  const [allLeads,          setAllLeads]          = useState(null);
  const [fichajesMes,       setFichajesMes]       = useState(null);
  const [comisionesAll,     setComisionesAll]     = useState(null);
  const mesActual = new Date().toISOString().slice(0, 7);
  const [loading,           setLoading]           = useState(true);
  const [loadingExtra,      setLoadingExtra]      = useState(false);
  const [error,             setError]             = useState('');
  const [lastLoad,          setLastLoad]          = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [ordSnap, facSnap] = await Promise.all([
        // SIN orderBy('fechaProgramada'): ese campo solo lo tienen los docs del
        // esquema app — ordenar por él dejaba fuera todas las órdenes crudas de
        // StelOrder (que usan `date`). Se ordena en cliente tras normalizar.
        getDocs(query(collection(db, 'ordenes'),  limit(8000))),
        getDocs(query(collection(db, 'facturas'), orderBy('fecha', 'desc'), limit(2000))).catch(() =>
          getDocs(query(collection(db, 'facturas'), limit(2000)))
        ),
      ]);
      // normalizeOrden unifica el doble esquema (app camelCase + StelOrder raw
      // kebab-case) → Informes analiza TODAS las órdenes, no solo las cerradas
      // desde la app.
      setAllOrdenes(
        ordSnap.docs
          .map(d => normalizeOrden({ id: d.id, ...d.data() }))
          .sort((a, b) => String(b.fechaProgramada || '').localeCompare(String(a.fechaProgramada || '')))
      );
      setAllFacturas(facSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLastLoad(new Date());
    } catch (e) {
      setError(`Error cargando datos: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  // Carga lazy de presupuestos y mantenimientos cuando se activa su tab
  useEffect(() => {
    if (activeTab === 'presupuestos' && allPresupuestos === null) {
      setLoadingExtra(true);
      fetch('/.netlify/functions/getPresupuestos', { headers: { ...authService.authHeader() } })
        .then(r => r.json())
        .then(d => setAllPresupuestos(d.success ? d.presupuestos : []))
        .catch(() => setAllPresupuestos([]))
        .finally(() => setLoadingExtra(false));
    }
    if (activeTab === 'mantenimientos_arr' && allMantenimientos === null) {
      setLoadingExtra(true);
      fetch('/.netlify/functions/getMantenimientos', { headers: authService.authHeader() })
        .then(r => r.json())
        .then(d => setAllMantenimientos(d.success ? d.mantenimientos : []))
        .catch(() => setAllMantenimientos([]))
        .finally(() => setLoadingExtra(false));
    }
    if (activeTab === 'fichajes_comision' && fichajesMes === null) {
      setLoadingExtra(true);
      Promise.all([
        fetch(`/.netlify/functions/getFichajes?mes=${mesActual}`, { headers: { ...authService.authHeader() } }).then(r => r.json()).catch(() => ({})),
        getDocs(query(collection(db, 'comisiones'), limit(4000))).then(s => s.docs.map(d => ({ id: d.id, ...d.data() }))).catch(() => []),
      ]).then(([fj, com]) => {
        setFichajesMes(fj.success ? (fj.fichajes || []) : []);
        setComisionesAll(com);
      }).finally(() => setLoadingExtra(false));
    }
    if (activeTab === 'marketing' && allLeads === null) {
      setLoadingExtra(true);
      getDocs(query(collection(db, 'leads'), orderBy('createdAt', 'desc'), limit(5000)))
        .catch(() => getDocs(query(collection(db, 'leads'), limit(5000))))
        .then(snap => setAllLeads(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
        .catch(() => setAllLeads([]))
        .finally(() => setLoadingExtra(false));
    }
  }, [activeTab, allPresupuestos, allMantenimientos, allLeads, fichajesMes, mesActual]);

  useEffect(() => { loadData(); }, [loadData]);

  const { start, end } = getPeriodDates(period);

  const ordenes  = allOrdenes.filter(o => {
    const d = o.fechaProgramada || o.createdAt || '';
    return d >= start && d <= end;
  });
  const facturas = allFacturas.filter(f => {
    const d = f.fecha || '';
    return d >= start && d <= end;
  });

  const TABS = [
    { id: 'resumen',           label: 'Resumen'        },
    { id: 'facturacion',       label: 'Facturación'    },
    { id: 'tecnicos',          label: 'Técnicos'       },
    { id: 'rentabilidad',      label: 'Rentabilidad'   },
    { id: 'servicios',         label: 'Servicios'      },
    { id: 'presupuestos',      label: 'Presupuestos'   },
    { id: 'mantenimientos_arr',label: 'ARR Contratos'  },
    { id: 'marketing',         label: 'Marketing'      },
    { id: 'fichajes_comision', label: 'Horas / Comisión' },
    { id: 'comparativa',       label: 'Comparativa'    },
  ];

  return (
    <div className="bg-gray-50 min-h-screen p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Informes y Analítica</h1>
          {lastLoad && (
            <p className="text-xs text-gray-400 mt-0.5">
              {allOrdenes.length} órdenes cargadas · Actualizado {lastLoad.toLocaleTimeString('es-ES')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {Object.entries(PERIOD_LABELS).map(([key, label]) => (
            <button key={key} onClick={() => setPeriod(key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                period === key
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-blue-300 hover:text-blue-600'
              }`}>
              {label}
            </button>
          ))}
          <button onClick={loadData} title="Actualizar"
            className="p-1.5 rounded-full bg-white border border-gray-200 text-gray-400 hover:text-blue-600 hover:border-blue-300 transition-colors">
            <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      {/* Period band */}
      <div className="flex items-center gap-2 text-xs text-gray-500 bg-white border border-gray-100 rounded-xl px-4 py-2.5">
        <span>📅</span>
        <span className="font-medium text-gray-700">{start}</span>
        <span>→</span>
        <span className="font-medium text-gray-700">{end}</span>
        <span className="ml-auto">
          <span className="font-semibold text-gray-700">{ordenes.length}</span> órdenes ·{' '}
          <span className="font-semibold text-gray-700">{facturas.filter(f => f.tipo !== 'abono').length}</span> facturas
        </span>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white border border-gray-100 rounded-xl p-1 w-fit shadow-sm">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-900'
            }`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20 gap-3 text-gray-500">
          <Spinner />
          <span className="text-sm">Cargando datos...</span>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-red-700 text-sm">{error}</div>
      ) : (
        <>
          {activeTab === 'resumen'           && <ResumenTab      ordenes={ordenes} facturas={facturas} />}
          {activeTab === 'facturacion'       && <FacturacionTab  facturas={facturas} />}
          {activeTab === 'tecnicos'          && <TecnicosTab     ordenes={ordenes} />}
          {activeTab === 'rentabilidad'      && <RentabilidadTab ordenes={ordenes} start={start} end={end} />}
          {activeTab === 'servicios'         && <ServiciosTab    ordenes={ordenes} />}
          {activeTab === 'presupuestos'      && <PresupuestosTab presupuestos={allPresupuestos || []} loading={loadingExtra || allPresupuestos === null} />}
          {activeTab === 'mantenimientos_arr'&& <MantenimientosARRTab mantenimientos={allMantenimientos || []} loading={loadingExtra || allMantenimientos === null} />}
          {activeTab === 'marketing'         && <MarketingTab leads={allLeads || []} loading={loadingExtra || allLeads === null} period={period} start={start} end={end} />}
          {activeTab === 'fichajes_comision' && <FichajesComisionTab fichajes={fichajesMes || []} comisiones={comisionesAll || []} loading={loadingExtra || fichajesMes === null} mes={mesActual} />}
          {activeTab === 'comparativa'       && <ComparativaTab allOrdenes={allOrdenes} allFacturas={allFacturas} start={start} end={end} />}
        </>
      )}
    </div>
  );
}
